// DataTable — shared table shell for admin / settings pages and any
// other long-list view in the dashboard.
//
// Replaces the hand-rolled sticky-header + grid-rows pattern previously
// duplicated across EndpointInventoryPage, UserManagementPage, and
// ProvisioningTokensPage. Every visual detail (header style, hover
// treatment, skeleton cadence, sticky-left footer trick) is pinned to
// the original EndpointInventoryPage implementation so existing pages
// look identical after migration.
//
// **Virtualization** is always on. Per-row heights are measured via
// ResizeObserver and cached by row id, so variable-height rows (the
// trace listing's context rows with dim-match pills, the endpoint
// inventory's installed-AI-agents wrap) work without configuration.
// Top/bottom spacer divs preserve the scrollbar so vertical scroll
// position is faithful even when only the visible slice is rendered.
// For small lists the cost is essentially zero (one ResizeObserver,
// linear-time range computation over a tiny array).
//
// Column state lives outside the component — callers create it once
// with `useDataTableColumns(...)` from `lib/columnSelection.js` and
// pass the result in as `cols`. That keeps the picker render-site
// flexible (usually AdminViewHeader's `actions` slot).
//
// Props:
//   cols            → result of useDataTableColumns(...)
//   rows            → array of row data (any shape)
//   getRowId        → row → stable key (default: r.id ?? array index)
//   renderCell      → (row, col) → ReactNode
//   renderFullRow   -> row -> ReactNode | null. A row it answers for draws
//                   that one node across every column in place of its
//                   cells (an opened row's detail beneath it).
//   loading         → true while a fetch is in flight
//   skeletonRows    → placeholder count on initial load
//   empty           → ReactNode for the empty state
//   footer          → ReactNode (e.g. Load-more) sticky-left at the bottom
//   onRowClick      → (row, event) → void; promotes each row to a <button>
//   rowAs           → override row tag explicitly ("div" | "button")
//   rowStyle        → row → inline style overrides (e.g. dynamic opacity)
//   rowClassName    → row → extra class string (e.g. active state)
//   showHeader      → render the column header row (default true)
//   estimatedRowHeight → initial guess used before measurement (default 32)
//   overscan        → rows rendered above/below the visible window (default 6)
//   findScrollContainer → (rowsNode) → Element | null. When provided,
//                   DataTable uses an external scroll container instead
//                   of creating its own (e.g. the trace log lives inside
//                   a page-level scroll surface). Skips the
//                   `flex-1 overflow-auto` wrapper. Without one, a table
//                   under a ScrollRoot (ui/scrollRoot.js) takes at most the
//                   room left below it in that surface and scrolls its own
//                   rows there, so what is above it stays in view.
//   (columns)       → a column with `fold: n` gives way, lowest n first, when
//                   the table is narrower than its columns, instead of the
//                   table scrolling sideways; it stays in the column picker
//                   and returns when there is room (lib/columnSelection.js
//                   foldColumns). The inner area's min-width is then what
//                   the shown columns need.
//   minWidth        → CSS min-width for the inner content area
//                   (default "100%"). Pages with a flex-stretch column
//                   that has no real intrinsic min size pass a pixel
//                   floor here so the table doesn't collapse.
//   maxHeight       → CSS max-height applied to the self-scroll wrapper.
//                   When set, the wrapper sizes to content up to this
//                   cap and scrolls beyond it. Only applies in
//                   self-scroll mode (`findScrollContainer` unset).
//                   Unitless number → px; string passed through.

import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ScrollRoot } from "./ui/scrollRoot.js";
import { columnsWidth, foldColumns, gridColsOf } from "../lib/columnSelection.js";
import ColumnResizeHandle from "./ColumnResizeHandle.jsx";
import ScrollArea from "./ScrollArea.jsx";
import { SkeletonBar } from "./ui/Skeleton.jsx";
import { EMPTY_WINDOW, bandsEqual, computeWindow, reconcileWindow, visibleBand, warnIndexKeyFallback, windowsEqual } from "../lib/virtualWindow.js";

// A fitted table's floor, and the room it leaves under itself (the page's
// bottom padding).
const FIT_MIN = 240;
const FIT_GAP = 28;
const DEFAULT_ROW_HEIGHT = 32;
const DEFAULT_OVERSCAN = 6;

// Pre-measurement visible band: nothing is known to be on screen yet.
const EMPTY_BAND = Object.freeze({ start: 0, end: 0, measured: false });

// Windowing math lives in ../lib/virtualWindow.js (pure + unit-tested). This
// hook owns only the imperative bits: resolving the scroll element, reading
// scrollTop / viewport height / baseline offset, and caching measured row
// heights. `scrollTop` is held in a ref rather than state so a scroll that
// stays inside the rendered overscan buffer triggers no re-render at all —
// reconcileWindow returns the same window object and we skip setRange.
function useVirtualizer({
    rowIds,
    rowsRef,
    selfScrollRef,
    findScrollContainer,
    estimatedRowHeight,
    overscan,
}) {
    const heights = useRef(new Map());
    const [scrollEl, setScrollEl] = useState(null);
    const [viewportH, setViewportH] = useState(0);
    const [baselineTop, setBaselineTop] = useState(0);
    const [range, setRange] = useState(EMPTY_WINDOW);

    const scrollTopRef = useRef(0);
    const rangeRef = useRef(range);
    rangeRef.current = range;

    // The visible band, tracked alongside the mounted window for callers that
    // report the reader's position. Refreshed on a geometry change and on a
    // scroll that moves the window — never on a scroll frame that stays inside
    // the overscan buffer, so the hysteresis win survives: a position readout
    // advances in overscan-sized steps instead of on every frame.
    const [band, setBand] = useState(EMPTY_BAND);
    const bandRef = useRef(band);
    bandRef.current = band;

    const count = rowIds.length;
    // Our overscan prop is in rows; the shared window math is pixel-native, so
    // convert through the row-height estimate.
    const overscanPx = overscan * estimatedRowHeight;

    const heightOf = useCallback(
        (i) => heights.current.get(rowIds[i]) ?? estimatedRowHeight,
        [rowIds, estimatedRowHeight],
    );

    // Refresh the visible band from live geometry. Called wherever the mounted
    // window is committed, so the two never disagree about the same scroll
    // position.
    const commitVisible = useCallback(() => {
        const next = visibleBand({
            scrollTop: scrollTopRef.current,
            baselineTop,
            viewportH,
            count,
            heightOf,
        });
        if (bandsEqual(bandRef.current, next)) return;
        bandRef.current = next;
        setBand(next);
    }, [baselineTop, viewportH, count, heightOf]);

    // Full recompute (fresh spacers + overscan). Used whenever the index space
    // or measured heights move under us — data changes, viewport resize, a row
    // measuring a new height — where the window must be rebuilt even if the
    // visible indices are unchanged.
    const recompute = useCallback(() => {
        const next = computeWindow({
            scrollTop: scrollTopRef.current,
            baselineTop,
            viewportH,
            count,
            heightOf,
            overscanPx,
        });
        // Value-equality guard: `idOf`/`rowIds` are fresh identities each render
        // when no getRowId is supplied, so this can run on every render. Only
        // commit when the window actually moved — otherwise we'd loop.
        commitVisible();
        if (windowsEqual(rangeRef.current, next)) return;
        rangeRef.current = next;
        setRange(next);
    }, [baselineTop, viewportH, count, heightOf, overscanPx, commitVisible]);

    // Hysteresis path. Used on scroll: keeps the current window (and skips the
    // re-render) while the visible band stays inside the rendered buffer.
    const reconcile = useCallback(() => {
        const prev = rangeRef.current;
        const next = reconcileWindow(prev, {
            scrollTop: scrollTopRef.current,
            baselineTop,
            viewportH,
            count,
            heightOf,
            overscanPx,
        });
        if (next !== prev) {
            rangeRef.current = next;
            setRange(next);
            commitVisible();
        }
    }, [baselineTop, viewportH, count, heightOf, overscanPx, commitVisible]);

    // Latest recompute, reachable from the dep-free ResizeObserver effect
    // without re-subscribing it on every recompute identity change.
    const recomputeRef = useRef(recompute);
    recomputeRef.current = recompute;

    // Rebuild the window whenever a geometry input changes (data, viewport,
    // baseline, overscan). Keyed on primitives + the row-id list rather than on
    // `recompute`'s identity so it doesn't fire every render; `recompute`'s
    // value-equality guard makes a redundant call a no-op regardless. Layout
    // effect so the windowed slice is correct before the browser paints.
    useLayoutEffect(() => {
        recomputeRef.current();
    }, [viewportH, baselineTop, count, overscanPx, rowIds]);

    // Resolve the scroll container — either DataTable's own wrapper
    // (default) or an ancestor located via the caller's predicate.
    //
    // Keyed on `count` because embedded mode resolves THROUGH the rows
    // container, and that ref only attaches once there are rows to render. A
    // table whose data arrives from a server therefore mounts empty, resolves
    // nothing, and — since every other dependency here is a stable identity (a
    // module-level predicate and two refs) — would never retry. The result is
    // silent: no scroll element means no measured viewport, `computeWindow`
    // takes its render-everything branch, and the surface mounts its entire row
    // set with no visible error. Self-scroll mode never hit this because it
    // resolves through DataTable's own wrapper, which exists before the data.
    useLayoutEffect(() => {
        if (findScrollContainer) {
            const node = rowsRef.current ? findScrollContainer(rowsRef.current) : null;
            setScrollEl(node || null);
            return;
        }
        // Self-scroll mode: the surface is a ScrollArea (overlayscrollbars),
        // so the element that actually scrolls is the OS viewport *inside*
        // the host, not the host div. ScrollArea is a child, so its OS-init
        // layout effect has already run by the time this (parent) effect
        // fires — the viewport exists. Mirror the lookup the embedded
        // consumers (TraceLogPanel / ContextPanel) use.
        const host = selfScrollRef.current;
        const viewport = host
            ? host.querySelector("[data-overlayscrollbars-viewport]")
                || host.querySelector(".os-viewport")
                || host
            : null;
        setScrollEl(viewport);
    }, [findScrollContainer, rowsRef, selfScrollRef, count]);

    // Embedded mode re-resolve. The layout effect above can land on the
    // pre-OverlayScrollbars host: on a fresh mount of the whole surface the
    // scroll ancestor's OS init is a *parent* layout effect, which runs after
    // this component's (child-first ordering). Once OS moves the content into
    // its generated viewport the host stops scrolling, so a listener left on
    // it never fires and the window freezes on the top band — blank space
    // where the unrendered rows should be. Passive effects run after the
    // entire tree's layout effects, so by now the real viewport exists;
    // re-resolve and swap if it differs. Keyed on `count` for the same reason as
    // the layout effect above: the rows container this resolves through does not
    // exist until there are rows.
    useEffect(() => {
        if (!findScrollContainer) return;
        const node = rowsRef.current ? findScrollContainer(rowsRef.current) : null;
        if (node) setScrollEl((prev) => (prev === node ? prev : node));
    }, [findScrollContainer, rowsRef, count]);

    useEffect(() => {
        if (!scrollEl) return undefined;
        const onScroll = () => {
            scrollTopRef.current = scrollEl.scrollTop;
            reconcile();
        };
        onScroll();
        scrollEl.addEventListener("scroll", onScroll, { passive: true });
        return () => scrollEl.removeEventListener("scroll", onScroll);
    }, [scrollEl, reconcile]);

    useEffect(() => {
        if (!scrollEl || typeof ResizeObserver === "undefined") return undefined;
        const sync = () => setViewportH(scrollEl.clientHeight);
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(scrollEl);
        return () => ro.disconnect();
    }, [scrollEl]);

    // baselineTop = the rows-container's distance from the top of the
    // scroll viewport, in scroll-content coordinates. Equivalent to
    // "header height" when the header sits above the rows; we measure
    // it directly so a sticky header doesn't have to be a special case.
    useLayoutEffect(() => {
        const rows = rowsRef.current;
        if (!rows || !scrollEl || typeof ResizeObserver === "undefined") return undefined;
        const sync = () => {
            const sRect = scrollEl.getBoundingClientRect();
            const rRect = rows.getBoundingClientRect();
            setBaselineTop(rRect.top - sRect.top + scrollEl.scrollTop);
        };
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(rows);
        // Observe the inner max-content wrapper too so a header
        // resize (e.g. column reflow) refreshes baselineTop.
        const inner = rows.parentElement;
        if (inner && inner !== scrollEl) ro.observe(inner);
        // Observe the scroll content root too: content *above* the rows
        // (a chart loading in, the trace toolbar's context/signals chip
        // rows toggling) moves the rows without resizing them, which
        // none of the observers above can see — the window then renders
        // offset by the stale delta. Any layout change inside the
        // scroller resizes the content root; sync() reads live geometry,
        // so spurious fires are cheap no-ops.
        const contentRoot = scrollEl.firstElementChild;
        if (contentRoot && contentRoot !== rows && contentRoot !== inner) {
            ro.observe(contentRoot);
        }
        return () => ro.disconnect();
    }, [scrollEl, rowsRef]);

    // Single shared observer; ref callback (re)attaches as rows mount.
    // Heights map is mutated directly (no React state per measurement); a
    // changed batch triggers one window recompute so the new spacer heights
    // take effect.
    const observerRef = useRef(null);
    useEffect(() => {
        if (typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver((entries) => {
            let changed = false;
            for (const entry of entries) {
                const id = entry.target.dataset?.rowId;
                if (!id) continue;
                // Border-box, to match measureRef's getBoundingClientRect
                // measurement — contentRect is content-box, which runs
                // ~11px short on padded/bordered rows and makes every row
                // fight between two cached heights.
                const h = entry.target.getBoundingClientRect().height;
                if (h > 0 && heights.current.get(id) !== h) {
                    heights.current.set(id, h);
                    changed = true;
                }
            }
            if (changed) recomputeRef.current();
        });
        observerRef.current = ro;
        return () => {
            ro.disconnect();
            observerRef.current = null;
        };
    }, []);

    const measureRef = useCallback((node) => {
        if (!node) return;
        const id = node.dataset?.rowId;
        if (!id) return;
        // Take an immediate measurement so the first paint after
        // mounting has correct heights without waiting for the RO tick.
        const h = node.getBoundingClientRect().height;
        if (h > 0 && heights.current.get(id) !== h) {
            heights.current.set(id, h);
            recomputeRef.current();
        }
        observerRef.current?.observe(node);
    }, []);

    return { range, band, measureRef };
}

export default function DataTable({
    cols,
    rows,
    getRowId,
    renderCell,
    renderFullRow = null,
    loading = false,
    skeletonRows = 4,
    empty = null,
    footer = null,
    onRowClick = null,
    rowAs = null,
    rowStyle = null,
    rowClassName = null,
    showHeader = true,
    // Optional click-to-sort. When `onSortChange` is supplied the header
    // cells become interactive (click / Enter / Space emits the column
    // id) and the active column shows a direction caret. `sortBy` is
    // `{ id, dir: "asc" | "desc" }` or null. The component is presentation-
    // only here — the caller owns the comparator and the sorted rows.
    sortBy = null,
    onSortChange = null,
    estimatedRowHeight = DEFAULT_ROW_HEIGHT,
    overscan = DEFAULT_OVERSCAN,
    findScrollContainer: ownScrollContainer = null,
    minWidth = "100%",
    maxHeight = null,
    // Optional. Called with `{ start, end, measured }` — the half-open row
    // index band the reader can actually SEE (no overscan), so a caller can
    // show a live "showing X-Y of N" position readout. `measured` is false
    // until the scroll viewport has been measured; the band carries no
    // information then, and a caller must not render a position from it.
    // Advances in overscan-sized steps, not on every scroll frame: a scroll
    // that uncovers no new rows re-reports nothing. Pass a stable identity (a
    // useState setter) — an inline function fires the effect every render.
    onVisibleRange = null,
}) {
    const { effective: allColumns, gridCols: allGridCols, widths } = cols;
    const findScrollContainer = ownScrollContainer;
    // Under a ScrollRoot, the table's height is capped at the room left below
    // its top in that surface (never under FIT_MIN), so the page above it
    // stays put and the rows scroll inside. Measured on every render (cheap)
    // and whenever the surface resizes.
    const fitRoot = useContext(ScrollRoot);
    const fits = !findScrollContainer && maxHeight == null && Boolean(fitRoot);
    const [fitHeight, setFitHeight] = useState(null);
    const selfScrollRef = useRef(null);
    const innerRef = useRef(null);

    // The width the table has, measured only when a column can fold.
    const folds = allColumns.some((c) => c.fold != null);
    const [room, setRoom] = useState(null);
    useLayoutEffect(() => {
        if (!folds) return undefined;
        const host = findScrollContainer ? innerRef.current?.parentElement : selfScrollRef.current;
        if (!host || typeof ResizeObserver === "undefined") return undefined;
        const measure = () => setRoom(host.clientWidth || null);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(host);
        return () => observer.disconnect();
    }, [folds, findScrollContainer]);
    const effective = useMemo(() => foldColumns(allColumns, room), [allColumns, room]);

    const measureFit = useCallback(() => {
        const root = fitRoot?.();
        const host = selfScrollRef.current;
        if (!root || !host) return;
        const space = Math.floor(root.getBoundingClientRect().bottom - host.getBoundingClientRect().top - FIT_GAP);
        setFitHeight((previous) => {
            const next = Math.max(FIT_MIN, space);
            return previous === next ? previous : next;
        });
    }, [fitRoot]);
    useLayoutEffect(() => { if (fits) measureFit(); });
    useEffect(() => {
        const root = fits ? fitRoot?.() : null;
        if (!root || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measureFit);
        observer.observe(root);
        window.addEventListener("resize", measureFit);
        return () => { observer.disconnect(); window.removeEventListener("resize", measureFit); };
    }, [fits, fitRoot, measureFit]);
    const gridCols = effective === allColumns ? allGridCols : gridColsOf(effective);
    // Stable row identity is required for correct windowing — the height cache
    // and React keys are both keyed by it, and an index key drifts the two
    // apart when rows reorder or filter (scroll jumps, wrong-height rows). Fall
    // back to the array index only as a last resort, and make it loud in dev so
    // the missing key is fixed rather than silently tolerated.
    const idOf =
        getRowId ||
        ((r, i) => {
            if (r?.id != null) return String(r.id);
            warnIndexKeyFallback("DataTable");
            return String(i);
        });

    const rowsRef = useRef(null);

    // Stable id list — derived once per render so the virtualizer
    // doesn't have to call getRowId multiple times during scroll.
    const rowIds = useMemo(() => rows.map((r, i) => String(idOf(r, i))), [rows, idOf]);

    const { range, band, measureRef } = useVirtualizer({
        rowIds,
        rowsRef,
        selfScrollRef,
        findScrollContainer,
        estimatedRowHeight,
        overscan,
    });

    useEffect(() => {
        if (onVisibleRange) {
            onVisibleRange({ start: band.start, end: band.end, measured: band.measured });
        }
    }, [onVisibleRange, band.start, band.end, band.measured]);

    // <button> rows when the row is interactive (Enter/Space + focus
    // ring come for free); <div> rows when not. Caller can override
    // explicitly via `rowAs`.
    const RowTag = rowAs || (onRowClick ? "button" : "div");
    const rowProps = RowTag === "button" ? { type: "button" } : {};

    const visible = rows.slice(range.start, range.end);

    // Inner container sizing depends on whether any column is
    // flex-stretch:
    //  - All pixel columns (admin tables): `width: max-content` so the
    //    inner sums to fixed widths, the trailing 1fr spacer absorbs
    //    slack when narrower than the viewport, and a horizontal
    //    scrollbar appears when wider. Header + rows lock-step.
    //  - Flex column present (trace listing): block-default width so
    //    the inner fills its parent and the flex track absorbs slack.
    //    A `max-content` here would let the flex column expand to its
    //    content's natural width and overflow the viewport, which —
    //    combined with the toolbar header's `overflow-hidden` — makes
    //    the rows appear to scroll independently of the columns.
    const hasFlex = effective.some((c) => c.flex);
    // Folding keeps the floor the caller asked for, and never lets the shown
    // columns squeeze below what they need.
    const innerMinWidth = folds ? `max(${minWidth}, ${columnsWidth(effective)}px)` : minWidth;
    const innerStyle = hasFlex
        ? { minWidth: innerMinWidth }
        : { minWidth: innerMinWidth, width: "max-content" };

    // aria-busy marks the whole table pending for assistive tech while a
    // fetch is in flight — first load (skeleton) and refetch (dimmed rows).
    const innerContent = (
        <div ref={innerRef} style={innerStyle} aria-busy={loading ? "true" : undefined}>
            {showHeader ? (
                <div
                    className="sticky top-0 z-10 grid items-center gap-x-2 border-b border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[6px] py-[6px] text-[12px] font-medium leading-[1.2] text-[color:var(--muted)]"
                    style={{ gridTemplateColumns: gridCols }}
                >
                    {effective.map((c) => {
                        const active = sortBy?.id === c.id;
                        return (
                            <span key={c.id} className="relative truncate">
                                {onSortChange && c.sortable !== false ? (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onSortChange(c.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                onSortChange(c.id);
                                            }
                                        }}
                                        className="inline-flex max-w-full cursor-pointer select-none items-center gap-[3px] align-middle transition-colors hover:text-[color:var(--ink)]"
                                    >
                                        <span className="truncate">{c.label}</span>
                                        {/* Caret reserves its slot even when inactive
                                            so toggling sort doesn't reflow the header. */}
                                        <span
                                            aria-hidden
                                            className={active ? "text-[color:var(--ink)]" : "opacity-0"}
                                        >
                                            {active && sortBy.dir === "asc" ? "↑" : "↓"}
                                        </span>
                                    </span>
                                ) : (
                                    c.label
                                )}
                                {/* Resize handle skipped on flex columns —
                                    they have no pixel width to drag. */}
                                {!c.flex ? (
                                    <ColumnResizeHandle
                                        currentWidth={c.width}
                                        onResize={(w) => widths.setWidth(c.id, w)}
                                    />
                                ) : null}
                            </span>
                        );
                    })}
                </div>
            ) : null}

            {rows.length > 0 ? (
                <div
                    ref={rowsRef}
                    style={{
                        opacity: loading ? 0.4 : 1,
                        transition: "opacity 180ms ease",
                        pointerEvents: loading ? "none" : undefined,
                    }}
                >
                    {range.topH > 0 ? <div style={{ height: range.topH }} /> : null}
                    {visible.map((r, sliceIdx) => {
                        const i = range.start + sliceIdx;
                        const id = rowIds[i];
                        const extraClass =
                            typeof rowClassName === "function" ? rowClassName(r) : "";
                        // DataTable callers use rowStyle only to fade contextual
                        // rows. Keep that API deliberately narrow instead of
                        // forwarding an opaque style object into the DOM.
                        const rowOpacity =
                            typeof rowStyle === "function" ? rowStyle(r)?.opacity : undefined;
                        const full = renderFullRow ? renderFullRow(r) : null;
                        return (
                            <RowTag
                                {...rowProps}
                                key={id}
                                ref={measureRef}
                                data-row-id={id}
                                onClick={onRowClick ? (e) => onRowClick(r, e) : undefined}
                                className={[
                                    "grid w-full items-center gap-x-2 border-t border-[color:var(--line-soft)] px-[6px] py-[5px] text-left",
                                    // Preflight already makes button rows transparent. Do not
                                    // repeat that utility here: it sorts after arbitrary
                                    // background utilities and erases caller-owned active /
                                    // context row fills.
                                    onRowClick ? "appearance-none cursor-pointer" : "",
                                    "hover:bg-[color:var(--control-bg)]",
                                    extraClass,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                style={{ gridTemplateColumns: gridCols, opacity: rowOpacity }}
                            >
                                {/* `min-w-0` only stops the track from growing:
                                    content wider than a fixed-px column painted
                                    past the row's right edge, ending the row border
                                    mid-content. Cells that need to escape the cell
                                    portal their overflow (OverflowTooltip). */}
                                {full != null ? (
                                    <div className="grid min-w-0 overflow-hidden" style={{ gridColumn: "1 / -1" }}>
                                        {full}
                                    </div>
                                ) : effective.map((c) => (
                                    <div key={c.id} className="grid min-w-0 overflow-hidden">
                                        {renderCell(r, c)}
                                    </div>
                                ))}
                            </RowTag>
                        );
                    })}
                    {range.bottomH > 0 ? <div style={{ height: range.bottomH }} /> : null}
                </div>
            ) : loading ? (
                // First load: placeholder rows on the real column grid, so
                // the header and rows don't shift when the data lands.
                <div data-skeleton-rows="">
                    <span className="sr-only">Loading…</span>
                    <div aria-hidden="true">
                        {Array.from({ length: skeletonRows }, (_, i) => (
                            <div
                                key={i}
                                className="grid h-[32px] items-center gap-x-2 border-t border-[color:var(--line-soft)] px-[6px]"
                                style={{ gridTemplateColumns: gridCols, opacity: Math.max(0.25, 1 - i * 0.12) }}
                            >
                                {effective.map((c, j) => (
                                    <SkeletonBar key={c.id} className={j ? "w-[60%]" : "w-[80%]"} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                empty
            )}

            {footer ? (
                <div style={{ position: "sticky", left: 0 }}>{footer}</div>
            ) : null}
        </div>
    );

    // Two layout modes:
    //  - Self-contained (default): DataTable owns the scroll surface,
    //    which is a ScrollArea (overlayscrollbars) so every admin /
    //    settings / inventory table gets the themed overlay scrollbars
    //    instead of the platform-native ones. The virtualizer measures
    //    against the OS viewport (see useVirtualizer above).
    //  - Embedded: caller's ancestor scrolls; we just render flat
    //    content. Used by the trace log so its toolbar/sort/header
    //    can stay sticky against the page-level scroll container (that
    //    ancestor is itself a ScrollArea).
    if (findScrollContainer) {
        return innerContent;
    }
    const selfScrollMaxHeight =
        maxHeight != null
            ? (typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight)
            : fits && fitHeight != null ? `${fitHeight}px` : undefined;
    // No `overscroll-behavior` here — `contain` made the inner scroll
    // halt at its top/bottom boundary, which felt like the scroll
    // "suddenly stopped" when the user expected the wheel to keep
    // moving them through the surrounding chat. Default chaining lets
    // the wheel flow into the ancestor scroll once the inner reaches
    // its boundary, which matches the read-as-continuous expectation.
    return (
        <ScrollArea
            hostRef={selfScrollRef}
            className="flex-1 min-h-0 min-w-0"
            maxHeight={selfScrollMaxHeight}
        >
            {innerContent}
        </ScrollArea>
    );
}
